import {EventEmitter} from '@angular/core';
import {isEmbeddedInIframe, isNext} from '../../core/utils/utils';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';

export class ScrollHelper {
  public static scrollTopTimeout = 0;
  public static offersDialogOpenTimeout = 0;
  static scrollToElement$: EventEmitter<ScrollToElementEvent> = new EventEmitter();
  stickyHeader: boolean = isNext();
  private readonly disabledScrollingClass = 'disabled-scrolling';
  private readonly scrollTopOffset = this.stickyHeader ? 45 : 0;

  get globalStickyContentSelector(): string | null {
    return this.stickyHeader ? '.wizard__sticky' : null;
  }

  get globalStickyElementHeight(): number {
    if (isNotNullOrUndefined(this.globalStickyContentSelector)) {
      const element = <HTMLElement>document.querySelector(this.globalStickyContentSelector);
      return element ? element.offsetHeight : 0;
    }
    return 0;
  }

  scrollTo(elementId: string, timeout: number = 0): void {
    if (isEmbeddedInIframe()) {
      setTimeout(() => {
        ScrollHelper.scrollToElement$.emit({
          selector: `.${elementId}`,
          offset: 50
        });
      }, timeout);
    } else {
    setTimeout(() => {
        let element = document.querySelector(`.${elementId}`);
        if (element) {
          const parent = this.findParent(element);
          const totalOffset = this.globalStickyElementHeight + this.scrollTopOffset;
          if (isNotNullOrUndefined(this.globalStickyContentSelector)) {
            this.scrollIntoViewWithOffset(parent, totalOffset, {behavior: 'smooth'});
          } else {
            parent.scrollIntoView({behavior: 'smooth'});
          }
        }
    }, timeout);
    }
  }

  scrollToTop(): void {
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, ScrollHelper.scrollTopTimeout);
  }

  scrollToXY(x: number, y: number): void {
    window.scrollTo(x, y);
  }

  scrollToBottom(): void {
    window.scrollTo(0, document.body.scrollHeight);
  }

  enableScrolling(): void {
    document.body.classList.remove(this.disabledScrollingClass);
  }

  disableScrolling(): void {
    document.body.classList.add(this.disabledScrollingClass);
  }

  scrollIntoView(selector: string, options?: ScrollIntoViewOptions, addScrollTopOffset: boolean = true) {
    if (isEmbeddedInIframe()) {
      ScrollHelper.scrollToElement$.emit({
        selector: selector,
        offset: 50
      });
    } else {
      if (isNotNullOrUndefined(this.globalStickyContentSelector)) {
        const totalOffset = addScrollTopOffset ? this.globalStickyElementHeight + this.scrollTopOffset : this.globalStickyElementHeight;
        this.scrollIntoViewWithOffset(document.querySelector(selector), totalOffset, options);
      } else {
        document.querySelector(selector).scrollIntoView(options);
      }
    }
  }

  scrollIntoViewWithOffset(target: HTMLElement, offset: number, options?: ScrollIntoViewOptions): void {
    if (target) {
      const targetPosition  = target.getBoundingClientRect().top;
      const y = targetPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: y,
        ...options
      });
    }
  }

  registerElementVisibility(selector: string, visibilityEvent: EventEmitter<boolean>) {
    if ('IntersectionObserver' in window && !!document.querySelector(selector)) {
      const observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting === true) {
          visibilityEvent.emit(true);
        } else {
          visibilityEvent.emit(false);
        }
      }, { threshold: [0.01] });

      observer.observe(document.querySelector(selector));
    } else {
      console.warn('IntersectionObserver API is not supported');
    }
  }

  registerScrollEventVisibiltyHandler(config: ScrollEventVisibiltyHandlerConfig): void {
    const { elementToHideSelector, visibleElementSelector, threshold } = config;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function(entries) {
        const elementToHide = document.querySelector(elementToHideSelector);
        if (entries[0].isIntersecting === true) {
          if (elementToHide) {
            elementToHide.classList.add('display-none');
          }
        } else {
          if (elementToHide) {
            elementToHide.classList.remove('display-none');
          }
        }
      }, { threshold: [threshold] });

      observer.observe(document.querySelector(visibleElementSelector));
    } else {
      console.warn('IntersectionObserver API is not supported');
    }
  }

  isInViewport(selector: string): boolean {
    const element = document.querySelector(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    return false;
  }

  private findParent(el) {
    if (el.parentElement.parentElement.parentElement.parentElement.tagName === 'MAT-FORM-FIELD') {
      return el.parentElement.parentElement.parentElement.parentElement;
    } else {
      return el.parentElement.parentElement;
    }
  }
}

export interface ScrollEventVisibiltyHandlerConfig {
  elementToHideSelector: string; // element to hide when visible element enters the viewport
  visibleElementSelector: string; // element to hide is hidden when this element enters the viewport
  threshold: number; // Intersection Observer API threshold
}

export interface ScrollToElementEvent {
  selector: string;
  offset?: number;
}
